import type { AuthStackParamList } from '@navigation/AuthStack';
import type { CommonStackParamList } from '@navigation/CommonStack';
import type { FollowUsersStackParamList } from '@navigation/FollowUsersStack';
import type { GroupStackParamList } from '@navigation/GroupStack';
import type { HomeStackParamList } from '@navigation/HomeStack';
import type { OnboardedStackParamList } from '@navigation/OnboardedStack';
import type { OnboardedTabParamList } from '@navigation/OnboardedTab';
import type { OnboardingStackParamList } from '@navigation/OnboardingStack';
import {
  AuthRoute,
  CommonRoute,
  FollowUsersRoute,
  GroupRoute,
  HomeRoute,
  OnboardedStackRoute,
  OnboardedTabRoute,
  OnboardingRoute,
} from '@navigation/Routes';
import { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import { CompositeScreenProps } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';

export { ViewFollowsScreenToggleState, ViewPlanScreenToggleState } from '../types/toggle';

export type RootNavigationParamList = AuthStackParamList &
  CommonStackParamList &
  FollowUsersStackParamList &
  GroupStackParamList &
  HomeStackParamList &
  OnboardedStackParamList &
  OnboardedTabParamList &
  OnboardingStackParamList;

// ================================================
// Auth
// ================================================

export type LoginScreenProps = StackScreenProps<AuthStackParamList, AuthRoute.Login>;

export type RegisterScreenProps = StackScreenProps<AuthStackParamList, AuthRoute.Register>;

export type ResetPasswordScreenProps = StackScreenProps<
  AuthStackParamList,
  AuthRoute.ResetPassword
>;

export type StartScreenProps = StackScreenProps<AuthStackParamList, AuthRoute.Start>;

// ================================================
// Onboarding
// ================================================

export type CreateUserScreenProps = StackScreenProps<
  OnboardingStackParamList,
  OnboardingRoute.CreateUser
>;

export type RequestPermissionsScreenProps = StackScreenProps<
  OnboardingStackParamList,
  OnboardingRoute.RequestPermissions
>;

export type WelcomeScreenProps = StackScreenProps<
  OnboardingStackParamList,
  OnboardingRoute.Welcome
>;

export type UserPrivateFormScreenProps = StackScreenProps<
  OnboardingStackParamList,
  OnboardingRoute.UserPrivateForm
>;

// ================================================
// Create Plan
// ================================================

export type CreatePlanScreenProps = StackScreenProps<
  OnboardedStackParamList,
  OnboardedStackRoute.CreatePlan
>;

// ===============
// Tabs
// ===============
export type HomeScreenProps = CompositeScreenProps<
  StackScreenProps<HomeStackParamList, HomeRoute.Home>,
  CompositeScreenProps<
    BottomTabScreenProps<OnboardedTabParamList>,
    StackScreenProps<OnboardedStackParamList>
  >
>;

export type ChatThreadsScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.ChatThreads>,
  CommonStackProps
>;

export type ViewGroupsScreenProps = CompositeScreenProps<
  StackScreenProps<GroupStackParamList, GroupRoute.ViewGroups>,
  CompositeScreenProps<
    BottomTabScreenProps<OnboardedTabParamList, OnboardedTabRoute.Groups>,
    StackScreenProps<OnboardedStackParamList>
  >
>;

export type FollowUsersScreenProps = CompositeScreenProps<
  StackScreenProps<FollowUsersStackParamList, FollowUsersRoute.FollowUsers>,
  CompositeScreenProps<
    BottomTabScreenProps<OnboardedTabParamList, OnboardedTabRoute.FollowUsers>,
    StackScreenProps<OnboardedStackParamList>
  >
>;

export type CurrentUserProfileScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.CurrentUserProfile>,
  CommonStackProps
>;

// ===============
// Common
// ===============
type CommonStackProps = CompositeScreenProps<
  BottomTabScreenProps<OnboardedTabParamList>,
  StackScreenProps<OnboardedStackParamList>
>;

export type ChatScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.Chat>,
  CommonStackProps
>;

export type CreateChatScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.CreateChat>,
  CommonStackProps
>;

export type NotificationScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.Notifications>,
  CommonStackProps
>;

export type ProfileScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.Profile>,
  CommonStackProps
>;

export type UpdatePlanScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.UpdatePlan>,
  CommonStackProps
>;

export type ViewPlanScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.ViewPlan>,
  CommonStackProps
>;

export type ViewFollowsScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.ViewFollows>,
  CommonStackProps
>;

export type ViewGroupScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.ViewGroup>,
  CommonStackProps
>;

export type ViewGroupMembersScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.ViewGroupMembers>,
  CommonStackProps
>;

export type UpdateGroupScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.UpdateGroup>,
  CommonStackProps
>;

export type UpdateUserScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.UpdateUser>,
  CommonStackProps
>;

export type CreateGroupScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.CreateGroup>,
  CommonStackProps
>;

export type SettingsScreenProps = CompositeScreenProps<
  StackScreenProps<CommonStackParamList, CommonRoute.Settings>,
  CommonStackProps
>;

// ================
// Helpers
// ================
export type NavigateProps<ParamList, RouteName extends keyof ParamList> = (
  screen: RouteName,
  params?: ParamList[RouteName]
) => void;
